import { checkIfTextExists, generateDateRangesFromFilter, sortByProperty, updateState } from "config/utils"
import { createSlice, PayloadAction, createSelector } from "@reduxjs/toolkit"
import {
	GetLocationDetailsInterface,
	CreateLocationDetailsInterface,
	GetAllLocationDetailsInterface,
	LocationStats,
	GetLocationStats,
	GetLocationTrends,
	LocationTrends,
} from "@type/locations.types"
import { locationDetails } from "services"
import { AppThunk, RootState } from "store"
import { lowerCase } from "lodash"
// import { clearSchedule } from "store/Schedules/schedules.slice"
import { clearPostList } from "./locationPosts.slice"
import { clearContractList } from "./locationContracts.slice"
import { DashboardDateFilter, DATE_FORMATS } from "../../config/constants"
import { Moment } from "moment"
import { EventValue } from "rc-picker/lib/interface"

export interface LocationDetailsStateInterface {
	list: GetLocationDetailsInterface[]
	inactiveLocationList: GetLocationDetailsInterface[]
	activeLocationList: GetLocationDetailsInterface[]
	allLocationList: Array<GetAllLocationDetailsInterface>
	loading: boolean
	saving: boolean
	stats: LocationStats | null
	trends: LocationTrends | null
	statsLoading: boolean
	trendsLoading: boolean
	isAllLocationByBranchLoaded: { [branchId: string]: boolean } | null
}

const initialState: LocationDetailsStateInterface = {
	list: [],
	inactiveLocationList: [],
	activeLocationList: [],
	allLocationList: [],
	loading: false,
	saving: false,
	stats: null,
	trends: null,
	statsLoading: false,
	trendsLoading: false,
	isAllLocationByBranchLoaded: null,
}

const locationDetailsSlice = createSlice({
	name: "locationDetails",
	initialState,
	reducers: {
		fetchingLocations: location => {
			location.loading = true
		},
		locationsFetched: (location, action: PayloadAction<GetLocationDetailsInterface[]>) => {
			location.loading = false
			location.list = [...action.payload]
		},
		inActiveLocationsFetched: (state, action: PayloadAction<GetLocationDetailsInterface[]>) => {
			state.loading = false
			const [updatedState] = updateState(state.inactiveLocationList, action.payload, "_id")
			state.inactiveLocationList = updatedState
		},
		activeLocationsFetched: (state, action: PayloadAction<GetLocationDetailsInterface[]>) => {
			state.loading = false
			const [updatedState] = updateState(state.activeLocationList, action.payload, "_id")
			state.activeLocationList = updatedState
		},
		allLocationsFetched: (location, action: PayloadAction<GetAllLocationDetailsInterface[]>) => {
			location.loading = false
			location.allLocationList = action.payload.sort(sortByProperty("name"))
		},
		locationsFetchingFailed: location => {
			location.loading = false
		},
		savingLocation: locationData => {
			locationData.saving = true
		},
		locationSaved: (state, action: PayloadAction<GetLocationDetailsInterface>) => {
			state.saving = false

			if (action.payload.isActive ?? true) {
				const [updatedState] = updateState(state.activeLocationList, action.payload, "_id")
				state.activeLocationList = updatedState
				state.activeLocationList.sort(sortByProperty("name"))
			} else {
				const [updatedState] = updateState(state.inactiveLocationList, action.payload, "_id")
				state.inactiveLocationList = updatedState
				state.inactiveLocationList.sort(sortByProperty("name"))
			}
		},
		updateLocationStatusOnContractUpdate: (
			state,
			action: PayloadAction<{ locationId: string; isActive: boolean }>,
		) => {
			state.saving = false
			const { locationId, isActive } = action.payload
			const { activeLocationList, inactiveLocationList } = state

			const foundSite = [...activeLocationList, ...inactiveLocationList]?.find(loc => loc._id === locationId)

			if (!foundSite) return
			if (foundSite.isActive === isActive) return

			const updateSite = { ...foundSite, isActive }

			const sourceList = foundSite.isActive ? activeLocationList : inactiveLocationList
			const targetList = foundSite.isActive ? inactiveLocationList : activeLocationList

			const newSourceList = sourceList.filter(list => list._id !== locationId)

			const [newTargetList] = updateState(targetList, updateSite, "_id")

			newTargetList.sort(sortByProperty("name"))

			if (foundSite.isActive) {
				state.activeLocationList = newSourceList
				state.inactiveLocationList = newTargetList
			} else {
				state.inactiveLocationList = newSourceList
				state.activeLocationList = newTargetList
			}
		},
		locationSavingFailed: location => {
			location.saving = false
		},
		ClearLocationDetailsState: location => {
			location.list = []
			location.loading = false
			location.saving = false
			location.allLocationList = []
			location.activeLocationList = []
			location.inactiveLocationList = []
			location.allLocationList = []
		},
		setAllLocationsByBranchLoadedState: (
			state,
			action: PayloadAction<{ branchId: string; isAllLoaded: boolean }>,
		) => {
			if (action.payload.isAllLoaded) {
				state.isAllLocationByBranchLoaded = {
					...state.isAllLocationByBranchLoaded,
					[action.payload.branchId]: true,
				}
			}
		},
		fetchingLocationStats: location => {
			location.statsLoading = true
		},
		fetchingLocationTrends: location => {
			location.trendsLoading = true
		},
		fetchedLocationStats: (location, action: PayloadAction<LocationStats>) => {
			location.statsLoading = false
			location.stats = action.payload
		},
		fetchedLocationTrends: (location, action: PayloadAction<LocationTrends>) => {
			if (location.trends) {
				const existingTrendsList = location.trends.list || []
				const newTrendsList = action.payload.list || []
				const updatedTrendList = existingTrendsList.concat(
					newTrendsList.filter(item => item !== undefined && item !== null),
				)
				location.trends.list = updatedTrendList
			} else {
				location.trends = action.payload
			}
		},
		resetLocationTrends: location => {
			location.trends = null
		},
		fetchedLocationStatsFailed: location => {
			location.statsLoading = false
		},
		fetchedLocationTrendsFailed: location => {
			location.trendsLoading = false
		},
		fetchedLocationTrendsPassed: location => {
			location.trendsLoading = false
		},
	},
})

//REDUCER
export default locationDetailsSlice.reducer

//ACTIONS
export const {
	allLocationsFetched,
	fetchingLocations,
	locationsFetched,
	inActiveLocationsFetched,
	activeLocationsFetched,
	updateLocationStatusOnContractUpdate,
	locationsFetchingFailed,
	savingLocation,
	locationSaved,
	locationSavingFailed,
	setAllLocationsByBranchLoadedState,
	// setAllLocationLoadedState,
	ClearLocationDetailsState,
	fetchingLocationStats,
	fetchedLocationStats,
	fetchedLocationStatsFailed,
	fetchingLocationTrends,
	fetchedLocationTrends,
	fetchedLocationTrendsFailed,
	resetLocationTrends,
	fetchedLocationTrendsPassed,
} = locationDetailsSlice.actions

const getLocations = (): AppThunk => async (dispatch, getState) => {
	try {
		if (getState().location.details.list.length) return
		dispatch(fetchingLocations())
		const { data: locationResponse } = await locationDetails.getAllLocations()
		dispatch(locationsFetched(locationResponse))
	} catch (error) {
		dispatch(locationsFetchingFailed())
	}
}

const getAllLocations = (): AppThunk => async dispatch => {
	try {
		dispatch(fetchingLocations())
		const { data: locationResponse } = await locationDetails.getAllLocationsDetail()
		dispatch(allLocationsFetched(locationResponse))
	} catch (error) {
		dispatch(locationsFetchingFailed())
	}
}

const getLocationsByBranchId =
	(branchId: string, isActive: boolean, cb?: () => void): AppThunk =>
	async (dispatch, getState) => {
		try {
			const existingSites = getState().location.details[isActive ? "activeLocationList" : "inactiveLocationList"]
			const isAllLocationByBranchLoaded = getState().location.details.isAllLocationByBranchLoaded

			if (getState().location.details.loading) return
			if (existingSites.length) {
				const foundActiveSite = existingSites.find(site => site.branch._id === branchId)
				if (foundActiveSite && !!isAllLocationByBranchLoaded?.[branchId]) return
			}

			dispatch(clearContractList())
			dispatch(clearPostList())
			//TODO dispatch(clearSchedule())
			dispatch(fetchingLocations())

			const { data: locationResponse } = await locationDetails.getLocationByBranchId(branchId, isActive)
			dispatch(isActive ? activeLocationsFetched(locationResponse) : inActiveLocationsFetched(locationResponse))
			cb?.()
		} catch (error) {
			dispatch(locationsFetchingFailed())
		}
	}

const getLocationById =
	(id: string, cb?: (id: string) => void): AppThunk =>
	async (dispatch, getState) => {
		try {
			const list = [
				...getState().location.details.activeLocationList,
				...getState().location.details.inactiveLocationList,
			]
			const locationFound = list.find(item => item._id === id)

			if (locationFound) {
				cb?.(id)
				return
			}

			dispatch(fetchingLocations())
			const { data: locationResponse } = await locationDetails.getLocationDetailsById(id)
			dispatch(
				locationResponse.isActive
					? activeLocationsFetched([locationResponse])
					: inActiveLocationsFetched([locationResponse]),
			)

			cb?.(locationResponse._id)
		} catch (error) {
			dispatch(locationsFetchingFailed())
		}
	}

const saveLocation =
	(payload: CreateLocationDetailsInterface, cb?: (id: string) => void): AppThunk =>
	async dispatch => {
		let data = null
		try {
			const isBeingUpdated = !!payload?._id

			dispatch(savingLocation())
			if (isBeingUpdated) {
				data = await locationDetails.updateLocationDetails(payload._id ?? "", payload)
			} else {
				data = await locationDetails.createLocation(payload)
			}
			const { data: locationResponse } = data

			dispatch(locationSaved({ ...locationResponse }))
			cb?.(locationResponse._id)

			//dispatch(setSelectedLocation(locationResponse._id))
		} catch (error) {
			dispatch(locationSavingFailed())
		}
	}

const saveLocationBySockets =
	(payload: GetLocationDetailsInterface): AppThunk =>
	(dispatch, getState) => {
		if (getState().globalState.selectedLocationBranch === payload.branch._id) {
			dispatch(locationSaved({ ...payload }))
		}
	}

const getLocationStats =
	(payload: GetLocationStats): AppThunk =>
	async dispatch => {
		const { date, siteIds, activeOnly } = payload
		try {
			dispatch(fetchingLocationStats())
			const { data: locationStats } = await locationDetails.getLocationStats({ siteIds, date, activeOnly })
			dispatch(fetchedLocationStats(locationStats ?? null))
		} catch (error) {
			dispatch(fetchedLocationStatsFailed())
		}
	}

const getLocationTrends =
	(payload: GetLocationTrends): AppThunk =>
	async dispatch => {
		const { date, siteIds, dateFilter, activeOnly } = payload
		try {
			dispatch(fetchingLocationTrends())
			const { data: locationTrends } = await locationDetails.getLocationTrends({
				siteIds,
				date,
				dateFilter,
				activeOnly,
			})
			dispatch(fetchedLocationTrends(locationTrends))
		} catch (error) {
			dispatch(fetchedLocationTrendsFailed())
		}
	}

export const getLocationTrendsInChunks =
	(
		filterType: DashboardDateFilter,
		finishTime: EventValue<Moment>,
		selectedLocations: string[],
		isAllBranchesSelected: boolean,
		activeOnly: boolean,
	): AppThunk =>
	async dispatch => {
		const dateRanges = generateDateRangesFromFilter(filterType, finishTime)

		dispatch(resetLocationTrends())
		const promises = dateRanges.map(({ startTime, finishTime }) => {
			return dispatch(
				getLocationTrends({
					dateFilter: filterType,
					...(!isAllBranchesSelected && { siteIds: selectedLocations }),
					date: {
						startTime: startTime.format(DATE_FORMATS.YYYY_MM_DD_T_HH_MM_SS1),
						finishTime: finishTime.format(DATE_FORMATS.YYYY_MM_DD_T_HH_MM_SS1),
					},
					activeOnly,
				}),
			)
		})

		try {
			dispatch(fetchingLocationTrends())
			await Promise.all(promises)
			dispatch(fetchedLocationTrendsPassed())
		} catch (error) {
			dispatch(fetchedLocationTrendsFailed())
		}
	}

// const selectSelectedLocationData = createSelector(
// 	(state: RootState) => ({
// 		selectedLocation: state.globalState.selectedLocation,
// 		locationList: state.location.details.list,
// 	}),
// 	({ selectedLocation = "", locationList }) => locationList.find(({ _id }) => _id === selectedLocation),
// )

export {
	getAllLocations,
	getLocations,
	saveLocation,
	getLocationById,
	saveLocationBySockets,
	getLocationsByBranchId,
	getLocationStats,
	getLocationTrends,
}

//SELECTORS
const selectLocationState = (state: RootState) => state.location.details
const isLocationLoading = () => (state: RootState) => selectLocationState(state).loading
const selectLocationList = () => (state: RootState) => selectLocationState(state).list
const isSavingLocation = () => (state: RootState) => selectLocationState(state).saving
const selectSelectedLocationStats = () => (state: RootState) => selectLocationState(state).stats
const selectSelectedLocationTrends = () => (state: RootState) => selectLocationState(state).trends?.list
const selectLocationStatsLoading = () => (state: RootState) => selectLocationState(state).statsLoading
const selectLocationTrendsLoading = () => (state: RootState) => selectLocationState(state).trendsLoading

const selectInactiveLocationList = () => (state: RootState) => selectLocationState(state).inactiveLocationList
const selectActiveLocationList = () => (state: RootState) => selectLocationState(state).activeLocationList

// const selectAllLocationsForDropdown = () => (state: RootState) =>
// 	selectLocationState(state)
// 		.activeLocationList?.map(({ _id, name }) => ({
// 			_id,
// 			name: name,
// 		}))
// 		.sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()))

const selectAllLocationsForDropdown = () =>
	createSelector(
		(state: RootState) => [
			...state.location.details.activeLocationList,
			...state.location.details.inactiveLocationList,
		],
		list =>
			list
				.map(({ _id, name }) => ({
					_id,
					name: name,
				}))
				.sort(sortByProperty("name")),
	)

const selectAllLocationDetails = () =>
	createSelector(
		(state: RootState) => [
			...state.location.details.activeLocationList,
			...state.location.details.inactiveLocationList,
		],
		list => list,
	)

const selectLocationListByBranchId = (branchId: string) =>
	createSelector(
		(state: RootState) => state.location.details.activeLocationList,
		activeLocationList => activeLocationList?.filter(i => i?.branch?._id == branchId),
	)

// USER ROLES
const selectSortedActiveSiteList = () =>
	createSelector(
		(state: RootState) => state.location.details.list,
		locationList => locationList?.filter(loc => !!loc?.isActive).sort(sortByProperty("name")),
	)

// REPORTS
const selectLocationListByField = (fieldName: string, fieldValue: string) => {
	return createSelector(
		(state: RootState) => state.location.details.allLocationList,
		locationList =>
			lowerCase(fieldName) && fieldValue
				? locationList?.filter(
						item => item?.[lowerCase(fieldName) as keyof GetAllLocationDetailsInterface] === fieldValue,
				  )
				: [],
	)
}

const selectLocationById = (locationId: string) =>
	createSelector(
		(state: RootState) => [...selectActiveLocationList()(state), ...selectInactiveLocationList()(state)],
		list => list?.find(location => location._id === locationId),
	)

const filteredListBySearch = (
	searchOn: string,
	branchId: string,
	searchOnFields: string[],
	customerId?: string,
	shouldShowActiveOnly?: boolean,
) => {
	return createSelector(
		(state: RootState) => [state.location.details.activeLocationList, state.location.details.inactiveLocationList],
		([activeList = [], inActiveList = []]) => {
			const sortedLocationList: GetLocationDetailsInterface[] = checkIfTextExists(
				(shouldShowActiveOnly ? activeList : inActiveList)?.filter((i: any) => {
					if (customerId === "all") {
						return i.branch._id === branchId
					}
					return i?.branch?._id === branchId && i?.customer?._id === customerId
				}),
				searchOn,
				searchOnFields,
			).sort((a: GetLocationDetailsInterface, b: GetLocationDetailsInterface) =>
				a.name.toLowerCase().localeCompare(b.name.toLowerCase()),
			)

			return sortedLocationList
		},
	)
}

export {
	filteredListBySearch,
	selectLocationListByBranchId,
	selectLocationList,
	selectAllLocationsForDropdown,
	selectLocationState,
	isSavingLocation,
	selectLocationById,
	isLocationLoading,
	selectLocationListByField,
	selectSelectedLocationStats,
	selectLocationStatsLoading,
	selectAllLocationDetails,
	// selectAddLocationClicked,
	selectSelectedLocationTrends,
	// selectSelectedLocationData,
	selectLocationTrendsLoading,
	selectSortedActiveSiteList,
	selectInactiveLocationList,
	selectActiveLocationList,
}
